import { useLayoutEffect, useRef, useState } from 'react'
import type { IndicatorType } from 'shared/types'
import { getIndicatorsColor } from '@/lib/utils'
import { coordinates } from '../shared'
import type { Radar } from 'shared'
import { usePreviousValue } from '@/hooks/use-previous-value'

const getOffset = (axis: string, type: IndicatorType, index: number, diff: number, distanceBetweenValues: number) => {
  const size = type === 'competences' ? 18 : type === 'vectors' ? 6 : 3

  const extraAngle = {
    competences: 80,
    vectors: 60,
    dimensions: 30,
  }

  const deg = (360 / size) * index
  const angle = ((deg - extraAngle[type]) / 180) * Math.PI

  const distanceDiff = diff * ((Math.PI * Math.PI) / (type === 'competences' ? 2 : type === 'vectors' ? 4 : 2))

  return axis === 'x'
    ? distanceDiff * Math.cos(angle) - distanceBetweenValues
    : axis === 'y'
      ? distanceDiff * Math.sin(angle)
      : 0
}

const modifyPosition = (
  axis: string,
  axisValue: number,
  coord: { index: number; type: IndicatorType; value: number; valueToCompare: number },
  valueIndex: string,
  radarOnSubSection = false,
  red?: boolean,
) => {
  const { index, type, value, valueToCompare } = coord

  if (valueIndex === 'valueToCompare') {
    const diff = radarOnSubSection ? value - 6 : value - valueToCompare
    const distanceBetweenValues = radarOnSubSection ? 9 - 1 : 9 + 5 // +5 for 2px more separation in compare mode
    return axisValue - getOffset(axis, type, index, diff, distanceBetweenValues)
  }
  if (radarOnSubSection) {
    const diff = value - 6
    const distanceBetweenValues = red ? -6 : 0
    return axisValue - getOffset(axis, type, index, diff, distanceBetweenValues)
  }
  return axisValue
}

type Coordinate = {
  key: string
  index: number
  type: string
  value: number
  valueToCompare: number
  color: string | undefined
  x: number
  y: number
  xText: number
  yText: number
}

const generateCoordinates = (
  indicators: Radar['indicators'],
  radarOnSubSection = false,
  compare?: boolean,
  red?: boolean,
) => {
  const types = [
    { type: 'competences', length: 18 },
    { type: 'vectors', length: 6 },
    { type: 'dimensions', length: 3 },
  ] as const

  const valueIndex = compare ? 'valueToCompare' : 'value'
  const valueIndexToCompare = compare ? 'value' : 'valueToCompare'

  const newCoordinates: Coordinate[] = []
  for (const { type, length } of types) {
    for (let i = 0; i < length; ++i) {
      const index = indicators[type].findIndex((elem) => elem.index === i)
      if (index !== -1) {
        const coordinateValue = i in coordinates ? coordinates[i as keyof typeof coordinates] : undefined
        const coordinateType =
          coordinateValue && type in coordinateValue ? coordinateValue[type as keyof typeof coordinateValue] : undefined
        const coordinateIndex = indicators[type][index]?.[valueIndex]
        const coordinate =
          coordinateType && coordinateIndex != null && coordinateIndex in coordinateType
            ? coordinateType[coordinateIndex]
            : undefined

        if (coordinate != null) {
          const indicatorEntry = indicators[type][index]
          const newCoordinate = {
            key: `${type}-${index}`,
            index: i,
            type,
            value: indicatorEntry?.[valueIndex] ?? 0,
            valueToCompare: indicatorEntry?.[valueIndexToCompare] ?? 0,
            color: getIndicatorsColor(type, i).text,
          }

          newCoordinates.push({
            ...newCoordinate,
            x: modifyPosition('x', coordinate.x, newCoordinate, valueIndex, radarOnSubSection, red),
            y: modifyPosition('y', coordinate.y, newCoordinate, valueIndex, radarOnSubSection, red),
            xText: modifyPosition('x', coordinate.xText, newCoordinate, valueIndex, radarOnSubSection, red),
            yText: modifyPosition('y', coordinate.yText, newCoordinate, valueIndex, radarOnSubSection, red),
          })
        }
      }
    }
  }

  return newCoordinates
}

// Easing function (ease-out quadratic)
function easeOut(t: number) {
  return 1 - (1 - t) * (1 - t)
}

/**
 * Position animated via SMIL, number text mutated imperatively by the parent's shared rAF loop.
 * `textRef` is destructured so `react/refs` does not taint the rest of `props` — docs/notes/app/radar.md.
 */
function ScoreItem({
  textRef,
  ...props
}: {
  elem: Coordinate
  fromX: number
  fromY: number
  red?: boolean
  ringed?: boolean
  textRef: (node: SVGTSpanElement | null) => void
}) {
  // Stable across re-renders so React writes the tspan once and the rAF count-up owns it — docs/notes/app/radar.md.
  const [initialNumber] = useState(() => String(props.elem.value))

  // A standing node is already placed by `transform`; skipping its SMIL drops 54 nodes — docs/notes/app/radar.md.
  const shouldAnimate = props.fromX !== props.elem.x || props.fromY !== props.elem.y

  return (
    <g
      id={`${props.red ? 'compare-' : ''}${props.elem.type}-${props.elem.index}-${props.elem.key}`}
      type={props.elem.type}
    >
      <circle
        transform={`translate(${props.elem.x.toPrecision(6)}, ${props.elem.y.toPrecision(6)})`}
        fill={props.red ? 'var(--color-radar-compare)' : 'var(--color-card)'}
        stroke={props.ringed ? 'var(--color-foreground)' : undefined}
        strokeWidth={props.ringed ? 0.1 : undefined}
        r="7"
      >
        {shouldAnimate && (
          <animateTransform
            attributeName="transform"
            type="translate"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={`${props.fromX.toPrecision(6)} ${props.fromY.toPrecision(6)}`}
            to={`${props.elem.x.toPrecision(6)} ${props.elem.y.toPrecision(6)}`}
          />
        )}
      </circle>
      <text
        transform={`translate(${props.elem.x.toPrecision(6)}, ${props.elem.y.toPrecision(6)})`}
        fill={props.red ? 'var(--accent-bg-text-color)' : 'var(--color-foreground)'}
        fontFamily="var(--font-sans)"
        fontSize="7.5"
        fontWeight="600"
        textAnchor="middle"
        dominantBaseline="central"
      >
        <tspan ref={textRef} dangerouslySetInnerHTML={{ __html: initialNumber }} />
        {shouldAnimate && (
          <animateTransform
            attributeName="transform"
            type="translate"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={`${props.fromX.toPrecision(6)} ${props.fromY.toPrecision(6)}`}
            to={`${props.elem.x.toPrecision(6)} ${props.elem.y.toPrecision(6)}`}
          />
        )}
      </text>
    </g>
  )
}

export function Scores(props: {
  radarOnSubSection?: boolean
  indicators: Radar['indicators']
  compare?: boolean
  red?: boolean
  // Ring each bubble in the foreground colour (the landing's hero's radar), so the shown profile's scores read apart.
  ringed?: boolean
}) {
  const nextCoordinates = generateCoordinates(props.indicators, props.radarOnSubSection, props.compare, props.red)

  // Position + value of every score, serialized: the only thing that counts as a change here.
  // `generateCoordinates` returns a fresh array every render, so identity can't be the change signal.
  const coordinatesKey = nextCoordinates.map((c) => `${c.key}:${c.x}:${c.y}:${c.value}`).join(',')

  // `newCoordinates` is the state-held array, so its identity is stable until the key changes — which is
  // what lets the animation effect below depend on it. `previousCoordinates` is where the scores were
  // last painted, i.e. the SMIL `from`. A remount (radar.tsx re-keys on the profile/compare boundary)
  // starts at `null`, so the scores arrive in place without tweening.
  const { current: newCoordinates, previous: previousCoordinates } = usePreviousValue(nextCoordinates, coordinatesKey)

  // O(1) lookup map for previous coordinates instead of O(n) find() per item. Withheld on a SUBSECTION radar,
  // which carries no lines for the bubbles to travel with (radar.tsx), so each one glided alone and read as
  // drift: with no `from` the bubble arrives in place and only its number counts up.
  const prevMap = (() => {
    if (!previousCoordinates || props.radarOnSubSection) return null
    const map = new Map<string, Coordinate>()
    for (const c of previousCoordinates) map.set(c.key, c)
    return map
  })()

  const containerRef = useRef<SVGGElement>(null)

  // Map of text element refs keyed by coordinate key — used by the shared rAF
  // loop to imperatively update textContent (no per-item useState/useEffect).
  const textRefsMap = useRef(new Map<string, SVGTSpanElement | null>())

  // Track "from" values for the number animation so we can interpolate
  // from the previous displayed value (not just the previous target).
  const numberFromRef = useRef(new Map<string, number>())

  const rafRef = useRef(0)

  // LAYOUT, and keyed on the state-held coordinates so the count-up is not restarted mid-flight — docs/notes/app/radar.md.
  useLayoutEffect(() => {
    if (!containerRef.current) return

    // 1. Trigger all SMIL animateTransform elements (position animation)
    const smilAnimates = containerRef.current.querySelectorAll('animateTransform')
    smilAnimates.forEach((el) => el.beginElement())

    // 2. Animate number counting with a single shared rAF loop
    const pairs: { key: string; from: number; to: number }[] = []
    for (const c of newCoordinates) {
      const from = numberFromRef.current.get(c.key) ?? c.value
      if (from !== c.value) {
        pairs.push({ key: c.key, from, to: c.value })
      } else {
        // Already at target — just ensure textContent is correct
        const textEl = textRefsMap.current.get(c.key)
        if (textEl) textEl.textContent = String(c.value)
      }
    }

    if (pairs.length === 0) return

    cancelAnimationFrame(rafRef.current)
    const startTime = performance.now()
    const duration = 250

    const tick = (now: number) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const ease = easeOut(progress)

      for (const pair of pairs) {
        const current = Math.round(pair.from + (pair.to - pair.from) * ease)
        const textEl = textRefsMap.current.get(pair.key)
        if (textEl) textEl.textContent = String(current)
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        // Commit final values
        for (const pair of pairs) {
          numberFromRef.current.set(pair.key, pair.to)
        }
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [newCoordinates])

  return (
    <g ref={containerRef}>
      {newCoordinates.map((elem) => {
        const prevElem = prevMap?.get(elem.key)
        return (
          <ScoreItem
            key={elem.key}
            elem={elem}
            fromX={prevElem?.x ?? elem.x}
            fromY={prevElem?.y ?? elem.y}
            red={props.red}
            ringed={props.ringed}
            textRef={(node) => {
              textRefsMap.current.set(elem.key, node)
              // The number is rendered server-side (ScoreItem's tspan innerHTML), so we don't write
              // textContent here. We only seed the animation baseline once, so the first interactive
              // transition counts up from the currently-displayed value rather than snapping.
              if (node && !numberFromRef.current.has(elem.key)) {
                numberFromRef.current.set(elem.key, elem.value)
              }
            }}
          />
        )
      })}
    </g>
  )
}
